import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, output } from '@angular/core';
import { AraPopoverComponent, PopoverContent, PopoverSectionType } from '../popover/popover.component';
import { MatIcon } from '@angular/material/icon';
import { AraButtonComponent } from '../ara-button/ara-button.component';
import { Permission } from '../../../services/data/permission';
import { AuthenticationUser, AuthService, PermissionObject } from '@eadsysteme/ewsauthclient-v1';
import { PermissionsTableComponent } from 'src/app/components/permissions-table/permissions-table.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { PermissionsDialogComponent } from 'src/app/components/permissions-table/permissions-dialog/permissions-dialog.component';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import dayjs from 'dayjs';
import { SessionService } from '../../../services/session.service';
import { KeycloakService } from 'src/app/auth/keycloak.service';
import { SnackbarService } from '../../../services/snackbar.service';
import { PermissionsService } from '../../../services/permissions.service';
import { MatBadge } from '@angular/material/badge';

@Component({
	selector: 'ara-permission-widget',
	imports: [AraPopoverComponent, MatIcon, AraButtonComponent, TranslocoDirective, MatBadge],
	templateUrl: './ara-permission-widget.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './ara-permission-widget.component.scss',
})
export class AraPermissionWidgetComponent {
	private readonly dialog: MatDialog = inject(MatDialog);
	private readonly destroyRef: DestroyRef = inject(DestroyRef);
	public translocoService: TranslocoService = inject(TranslocoService);
	public sessionService: SessionService = inject(SessionService);
	private readonly keycloakService: KeycloakService = inject(KeycloakService);
	private readonly authService: AuthService = inject(AuthService);
	private readonly snackbarService: SnackbarService = inject(SnackbarService);
	protected readonly permissionsService: PermissionsService = inject(PermissionsService);

	readonly canOpenDialog = input<boolean>(true);
	readonly canCreatePermission = input<boolean>(true);
	readonly canModifyPermissions = input<boolean>(true);
	readonly permissionsDisabledReason = input<string>('');
	readonly permissions = input<Permission[]>(undefined);
	readonly dialogParameters = input<PermissionDialogParameters>(undefined);

	readonly permissionsChanged = output<void>();

	getPermissionBadgeLabel(): string | null {
		const permissions = this.permissions();
		if (!permissions || permissions.length < 1) {
			return null;
		}
		const total = permissions.length;
		const active = permissions.filter((permission) => !!permission?.codeActivatedOn).length;

		return `${active}/${total}`;
	}

	getPermissionCodesStatusPopover(): PopoverContent {
		const fallback = this.translocoService.translate('permissions.noPermissionsFound');
		const permissions = this.permissions();
		if (!permissions?.length) {
			return {
				sections: [
					{
						type: PopoverSectionType.TEXT,
						items: [fallback],
					},
				],
			};
		}
		const statusHeader = this.translocoService.translate('permissions.status');
		const valueHeader = this.translocoService.translate('permissions.codeOrName');
		const activatedLabel = this.translocoService.translate('permissions.statusActivated');
		const pendingLabel = this.translocoService.translate('permissions.statusPending');

		const rows = permissions.map((permission) => {
			const isActivated = !!permission?.codeActivatedOn;
			let value = '';
			if (isActivated) {
				const fullName = `${permission.firstName ?? ''} ${permission.lastName ?? ''}`.trim();
				value = fullName || permission.authCode || this.translocoService.translate('user.unknown');
			} else {
				value = permission.authCode || this.translocoService.translate('common.noData');
			}
			return [isActivated ? activatedLabel : pendingLabel, value];
		});
		return {
			sections: [
				{
					type: PopoverSectionType.TABLE,
					headers: [statusHeader, valueHeader],
					rows,
				},
			],
		};
	}

	openPermissionsDialog(event?: Event): void {
		event?.preventDefault();
		if (!this.canOpenDialog()) {
			return;
		}

		const dialogRef = this.dialog.open(PermissionsTableComponent, {
			width: '1300px',
			maxWidth: '85vw',
			disableClose: false,
			autoFocus: false,
			restoreFocus: false,
		});

		const dialogParameters = this.dialogParameters();
		const instance = dialogRef.componentInstance;
		instance.currentUserId = dialogParameters?.currentUserId;
		instance.tenantUnitId = dialogParameters?.tenantUnitId ?? '';
		instance.realEstateId = dialogParameters?.realEstateId ?? '';
		instance.permissionType = dialogParameters?.permissionType;
		instance.personEmail = dialogParameters?.personEmail ?? '';
		const canCreatePermission = this.canCreatePermission();
		instance.canAddPermission = canCreatePermission;
		instance.canModifyPermissions = this.canModifyPermissions();
		instance.showPermissionTypeSelect().set(dialogParameters?.showPermissionTypeSelect);
		instance.disablePermissionTypeSelect().set(dialogParameters?.disablePermissionTypeSelect);

		if (!canCreatePermission) {
			instance.permissionsDisabledReason = this.permissionsDisabledReason();
		}

		// The generic dialog always closes with an undefined result (header-X/backdrop/ESC), so read the
		// accumulated change flag straight off the (captured) component instance instead of the result.
		dialogRef
			.afterClosed()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				if (instance.dataChanged) {
					this.permissionsChanged.emit();
				}
			});
	}

	openCreatePermissionDialog(): void {
		if (!this.canCreatePermission()) {
			return;
		}

		const dialogRef = this.dialog.open(PermissionsDialogComponent, {
			minWidth: '300px',
			autoFocus: false,
			restoreFocus: false,
		});

		dialogRef.componentInstance.title.set(this.translocoService.translate('permissions.createPermission'));
		dialogRef.componentInstance.isEdit.set(false);
		dialogRef.componentInstance.personEmail = this.dialogParameters()?.personEmail ?? '';
		dialogRef.componentInstance.permission = this.createNewPermission();

		dialogRef
			.afterClosed()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((result) => {
				if (result?.saved && result?.permission) {
					this.savePermission(result.permission, result.sendMail, result.type);
				}
			});
	}

	private savePermission(permission: Permission, sendMail = false, type: PermissionObject.PermissionTypeEnum = undefined): void {
		if (type) {
			permission.permissionType = type;
		}

		const authUserTO: AuthenticationUser = this.permissionsService.createAuthUser(permission);
		this.authService
			.generateAuthCodeForRealEstate(this.sessionService.effectiveArea, sendMail, authUserTO)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				error: (err) => this.snackbarService.apiError(err, this.translocoService.translate('errors.permissionCreate')),
				complete: () => {
					this.permissionsChanged.emit();
				},
			});
	}

	private createNewPermission(): Permission {
		const dialogParameters = this.dialogParameters();
		return {
			keyCloakUserId: undefined,
			firstName: undefined,
			lastName: undefined,
			authCode: undefined,
			createDate: new Date().toDateString(),
			permissionType: dialogParameters?.permissionType,
			permissionUserId: dialogParameters?.currentUserId,
			permissionCreatorId: this.keycloakService.getKeycloakInstance().tokenParsed?.sub,
			tenantUnitId: dialogParameters?.tenantUnitId ?? '',
			area: this.sessionService.effectiveArea,
			email: undefined,
			comment: undefined,
			codeActivatedOn: undefined,
			codeValidUntil: dayjs().add(1, 'month').toDate().toDateString(),
		};
	}
}

export class PermissionDialogParameters {
	currentUserId: string;
	permissionType: PermissionObject.PermissionTypeEnum;
	tenantUnitId?: string;
	realEstateId?: string;
	personEmail?: string;
	showPermissionTypeSelect?: boolean = false;
	disablePermissionTypeSelect?: boolean = false;
}
